const PROGRESS_STORAGE_KEY = 'warboys.campaignProgress';

interface PersistedProgress {
  missionNumber: number;
  lastSavedAt: string;
}

/** Persists and restores campaign progress via localStorage. */
export const ProgressStore = {
  /** Write the current missionNumber to localStorage. */
  save(missionNumber: number): void {
    try {
      const data: PersistedProgress = {
        missionNumber,
        lastSavedAt: new Date().toISOString(),
      };
      localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(data));
    } catch (_) {
      // Private/incognito mode or storage quota exceeded — silently ignore.
    }
  },

  /** Read the saved mission number. Returns null if no saved progress exists. */
  load(): number | null {
    try {
      const raw = localStorage.getItem(PROGRESS_STORAGE_KEY);
      if (!raw) return null;
      const data: PersistedProgress = JSON.parse(raw);
      if (typeof data.missionNumber === 'number' && data.missionNumber >= 1) {
        return data.missionNumber;
      }
    } catch (_) {
      // Corrupted data — treat as no saved progress.
    }
    return null;
  },

  /** Remove saved progress (New Game / full wipe). */
  clear(): void {
    try {
      localStorage.removeItem(PROGRESS_STORAGE_KEY);
    } catch (_) { /* ignore */ }
  },
};

export interface SoldierRecord {
  name: string;
  alive: boolean;
}

/** Predefined pool of soldier names (first 4 are initial squad). */
export const SOLDIER_NAME_POOL: string[] = [
  'Baker',
  'Carter',
  'Davis',
  'Evans',
  'Foster',
  'Garcia',
  'Harris',
  'Ingram',
  'Jones',
  'Kelly',
  'Lewis',
  'Moore',
  'Nelson',
  'Owen',
  'Price',
  'Quinn',
  'Reed',
  'Stone',
  'Turner',
  'Underwood',
];

function buildInitialRoster(): SoldierRecord[] {
  return SOLDIER_NAME_POOL.slice(0, 4).map((name) => ({ name, alive: true }));
}

export const CampaignState = {
  missionNumber: 1,
  roster: buildInitialRoster() as SoldierRecord[],
  fallen: [] as SoldierRecord[],

  /** Reset to initial state (used after full wipe) and clear saved progress. */
  reset(): void {
    this.missionNumber = 1;
    this.roster = buildInitialRoster();
    this.fallen = [];
    ProgressStore.clear();
  },

  /** Mark a soldier dead by name. Returns the record or null if not found. */
  killSoldier(name: string): SoldierRecord | null {
    const record = this.roster.find((r) => r.name === name && r.alive);
    if (!record) return null;
    record.alive = false;
    this.fallen.push(record);
    return record;
  },

  /** Living soldiers only. */
  getLiving(): SoldierRecord[] {
    return this.roster.filter((r) => r.alive);
  },

  /**
   * After mission success: increment mission number and top up roster to 2
   * if it has fallen below that threshold.
   */
  advanceMission(): void {
    this.missionNumber++;
    const living = this.getLiving();
    if (living.length < 2) {
      // Find the next unused name
      const usedNames = new Set(this.roster.map((r) => r.name));
      for (const name of SOLDIER_NAME_POOL) {
        if (!usedNames.has(name)) {
          const recruit: SoldierRecord = { name, alive: true };
          this.roster.push(recruit);
          usedNames.add(name);
          if (this.getLiving().length >= 2) break;
        }
      }
    }
  },
};
